// The calendar on a phone (SPhoneCal, with one change Kareem asked for on 4 Nov: an agenda instead of a single day).
// The header (the year and week number, the month, Previous and Next week, Today), the week strip with dots and the
// type chips stay. Below the strip: ONE scrolling agenda, each day with something on it under a sticky header
// ("Thursday 29 Oct", and "Today"), its items as rows (time, a colour bar, the title, then type, track and owner).
// It starts at today and loads the coming weeks as you scroll down; scrolling up shows the past weeks loaded, and
// "Show earlier days" at the top loads four more (on a tap, not on reaching the top: the chips live up there, and
// days arriving above them would push them away). The header and the strip stay at the top and follow the list
// (the day at the top is highlighted, its month and week shown); tapping a day scrolls the list to it
// (or to the next day with something on, saying "Nothing on Tue 3 Nov"); the arrows move a week. A row opens a sheet:
// type, Milestone, Waiting on, When, Track, Space, Owner, Open activity, Ask about it. View: colour by type or track,
// the space, Only mine. No month grid on the phone.
import { useActivityDrawer } from '../shell/Dock';
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { AnimatePresence, useReducedMotion } from 'motion/react';
import { useQueries } from '@tanstack/react-query';
import type { CalendarItem, CalendarKind } from '@dpw/shared';
import { api } from '../api';
import { useSpaces } from '../lib/queries';
import { useRemembered } from '../lib/theme';
import { addDays, dayLabel, localDate, stripIsoWeek } from '../lib/format';
import { tyColor } from '../ui/bits';
import { SkelList } from '../ui/motion';
import { byTime, longDay, typeWord, useColours, weekStart, type ColourBy } from '../pages/Calendar';
import { BellButton, P, PSheet, Seg, Tag, Title, TopBar } from './kit';
import { useAssistantPanel } from '../assistant/Panel';

const KINDS: [CalendarKind, string][] = [['meeting', 'Meetings'], ['workshop', 'Workshops'], ['deliverable', 'Deliverables'], ['task', 'Tasks'], ['reminder', 'Reminders']];
const DOW1 = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
/** How far the agenda reaches each time it loads more (days). */
const STEP = 28;
const utc = (d: string) => new Date(`${d}T12:00:00Z`);
const isDay = (d: string | null): d is string => !!d && /^\d{4}-\d{2}-\d{2}$/.test(d);
export function PhoneCalendar() {
  const panel = useAssistantPanel();
  const today = localDate();
  const [params] = useSearchParams();
  const start = isDay(params.get('date')) ? params.get('date')! : today;
  const reduce = useReducedMotion();
  const [spaceId, setSpaceId] = useRemembered('dpw.calendar.space', '');
  const [mine, setMine] = useRemembered('dpw.calendar.mine', false);
  const [by, setBy] = useRemembered<ColourBy>('dpw.calendar.by', 'type');
  const [hidden, setHidden] = useRemembered<CalendarKind[]>('dpw.calendar.hidden', []);
  const [open, setOpen] = useState<CalendarItem | null>(null);
  // Open activity: the activity's full sheet (6.2), in place of this one.
  const acts = useActivityDrawer();
  const [viewing, setViewing] = useState(false);
  // Loaded in fixed blocks of STEP days counted from an anchor (the API gives at most 62 days a request), each its
  // own cached query: blocks [lo, hi]. Two to begin with: two weeks back to six weeks ahead.
  const [anchor] = useState(() => addDays(weekStart(start), -14));
  const [blocks, setBlocks] = useState({ lo: 0, hi: 1 });
  const blockOf = useCallback((d: string) => Math.floor((Date.parse(`${d}T12:00:00Z`) - Date.parse(`${anchor}T12:00:00Z`)) / 86_400_000 / STEP), [anchor]);
  const range = { from: addDays(anchor, blocks.lo * STEP), to: addDays(anchor, blocks.hi * STEP + STEP - 1) };
  const q = useQueries({
    queries: Array.from({ length: blocks.hi - blocks.lo + 1 }, (_, n) => {
      const from = addDays(anchor, (blocks.lo + n) * STEP), to = addDays(from, STEP - 1);
      return { queryKey: ['calendar', from, to, spaceId || undefined, mine], queryFn: () => api.calendar({ from, to, spaceId: spaceId || undefined, mine: mine ? 'true' : undefined }) };
    }),
    combine: rs => ({ data: rs.every(x => x.data) ? rs.flatMap(x => x.data!) : rs.some(x => x.data) ? null : undefined, isFetching: rs.some(x => x.isFetching), ready: rs.every(x => x.data) }),
  });
  // While a new block loads, keep showing what was there (null: some blocks are in, not all).
  const shown = useRef<CalendarItem[] | undefined>(undefined);
  if (q.data) shown.current = q.data;
  const all = useMemo(() => q.data ?? shown.current ?? [], [q.data]);
  const colours = useColours(all, by);
  const byDay = useMemo(() => {
    const m = new Map<string, CalendarItem[]>();
    for (const i of all) if (!hidden.includes(i.kind)) m.set(i.date, [...(m.get(i.date) ?? []), i]);
    for (const l of m.values()) l.sort(byTime);
    return m;
  }, [all, hidden]);
  const days = useMemo(() => [...byDay.keys()].sort(), [byDay]);
  const spaces = useSpaces().data ?? [];

  // The day at the top of the list (the strip highlights it and shows its week), and a quiet note after a jump.
  const [topDay, setTopDay] = useState(start);
  const [note, setNote] = useState<{ before: string | null; text: string } | null>(null);
  const page = useRef<HTMLDivElement>(null);
  const strip = useRef<HTMLDivElement>(null);
  const list = useRef<HTMLDivElement>(null);
  const scroller = () => page.current?.closest('.ph-main') as HTMLElement | null;
  const stripWeek = weekStart(topDay);
  const week = Array.from({ length: 7 }, (_, n) => addDays(stripWeek, n));
  // The strip's ISO 8601 week and its week-year (Monday to Saturday of the strip are in it).
  const isoWk = stripIsoWeek(stripWeek);

  // The strip's height, so the sticky day headers sit right under it and a jump lands below it.
  useLayoutEffect(() => {
    const el = strip.current, pg = page.current;
    if (!el || !pg) return;
    const set = () => pg.style.setProperty('--ph-strip-h', `${el.offsetHeight}px`);
    set();
    const ro = new ResizeObserver(set);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  /** Scroll so this element sits under the strip. */
  const reveal = useCallback((el: Element | null, smooth: boolean) => {
    el?.scrollIntoView({ block: 'start', behavior: smooth && !reduce ? 'smooth' : 'auto' });
  }, [reduce]);

  // A jump waits for its data when the day is not loaded yet.
  const lock = useRef(0);
  const pending = useRef<{ day: string; why: 'tap' | 'week' | 'start'; smooth: boolean } | null>({ day: start, why: 'start', smooth: false });
  const jump = useCallback((day: string, why: 'tap' | 'week' | 'start', smooth = true) => {
    if (day < range.from || day > addDays(range.to, -7)) {
      pending.current = { day, why, smooth };
      const k = blockOf(day);
      setBlocks(b => ({ lo: Math.min(b.lo, k), hi: Math.max(b.hi, k + 1) }));
      return;
    }
    pending.current = null;
    const target = days.find(d => d >= day) ?? null;
    const when = why === 'week' ? `in the week of ${dayLabel(day)}` : day === today ? 'today' : `on ${dayLabel(day)}`;
    const empty = why === 'week' ? !target || target > addDays(day, 6) : target !== day;
    setNote(empty ? { before: target, text: target ? `Nothing ${when}` : `Nothing ${when} or after it, in the weeks loaded` } : null);
    // The strip stays on the chosen day while the list scrolls there; then it follows the list again.
    lock.current = performance.now() + 900;
    setTopDay(target && !(why === 'week' && empty) ? target : day);
    // After the note renders: to the note when there is one, else to the day.
    requestAnimationFrame(() => reveal(list.current?.querySelector(empty ? '[data-note]' : `[data-day="${target}"]`) ?? null, smooth));
  }, [days, range.from, range.to, today, reveal, blockOf]);

  // Data arrived: do a jump that was waiting for it.
  const ready = q.ready;
  useEffect(() => {
    if (!ready || !pending.current) return;
    const p = pending.current;
    if (p.day >= range.from && p.day <= addDays(range.to, -7)) jump(p.day, p.why, p.smooth);
  }, [ready, range.from, range.to, jump]);

  // Scrolling: the strip follows the day at the top; near the end, load the next four weeks.
  useEffect(() => {
    const sc = scroller();
    if (!sc) return;
    let raf = 0;
    const onScroll = () => {
      cancelAnimationFrame(raf);
      raf = requestAnimationFrame(() => {
        const line = (strip.current?.getBoundingClientRect().bottom ?? 0) + 2;
        // The last section that has reached the strip; at the very top, before any has, the first one.
        const secs = [...(list.current?.querySelectorAll<HTMLElement>('[data-day]') ?? [])];
        let top: string | null = secs[0]?.dataset.day ?? null;
        for (const s of secs) {
          if (s.getBoundingClientRect().top <= line) top = s.dataset.day!; else break;
        }
        if (top && performance.now() > lock.current) setTopDay(top);
        if (q.isFetching) return;
        if (sc.scrollTop + sc.clientHeight > sc.scrollHeight - 900) setBlocks(b => ({ ...b, hi: b.hi + 1 }));
      });
    };
    sc.addEventListener('scroll', onScroll, { passive: true });
    return () => { sc.removeEventListener('scroll', onScroll); cancelAnimationFrame(raf); };
  }, [q.isFetching]);
  const [earlier, setEarlier] = useState(false);
  useEffect(() => { if (ready) setEarlier(false); }, [ready, blocks.lo]);

  const toggle = (k: CalendarKind) => setHidden(hidden.includes(k) ? hidden.filter(x => x !== k) : [...hidden, k]);
  const noteEl = (key: string) => note && <div key={key} className="ph-calnote" data-note role="status">{note.text}</div>;

  return (
    <div className="ph-page ph-calp" ref={page}>
      <TopBar>
        <button type="button" className="ph-nb" aria-label="View" onClick={() => setViewing(true)}><svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth={1.9} strokeLinecap="round" aria-hidden="true"><path d="M4 7h10M18 7h2M4 17h4M12 17h8" /><circle cx="16" cy="7" r="2" /><circle cx="10" cy="17" r="2" /></svg></button>
        <BellButton />
      </TopBar>
      <div className="ph-wkbar" ref={strip}>
        <div className="ph-mh">
          <Title md eyebrow={`${isoWk.year} · week ${isoWk.week}`} title={utc(topDay).toLocaleDateString('en-GB', { month: 'long', timeZone: 'UTC' })} />
          <button type="button" className="ph-todayb" onClick={() => jump(today, 'start')}>Today</button>
          <button type="button" className="ph-nb" aria-label="Previous week" onClick={() => jump(addDays(stripWeek, -7), 'week')} style={{ transform: 'scaleX(-1)' }}><P.chev /></button>
          <button type="button" className="ph-nb" aria-label="Next week" onClick={() => jump(addDays(stripWeek, 7), 'week')}><P.chev /></button>
        </div>
        <div className="ph-wk" role="tablist" aria-label="Days">
          {week.map(d => (
            <button key={d} type="button" role="tab" aria-selected={d === topDay} aria-label={dayLabel(d)} onClick={() => jump(d, 'tap')}
              className={`${d === topDay ? 'on' : ''}${d === today ? ' today' : ''}`}>
              <span className="w">{DOW1[utc(d).getUTCDay()]}</span><span className="n">{Number(d.slice(8))}</span>
              <span className="dots" aria-hidden="true">{(byDay.get(d) ?? []).slice(0, 3).map(i => <i key={`${i.kind}-${i.id}`} style={{ background: colours.c(i) }} />)}</span>
            </button>
          ))}
        </div>
      </div>
      <div className="ph-chips" role="group" aria-label="Show">
        {KINDS.map(([k, l]) => (
          <button key={k} type="button" className={`ph-chip${hidden.includes(k) ? '' : ' on'}`} aria-pressed={!hidden.includes(k)} onClick={() => toggle(k)}>
            <i style={{ background: tyColor(k) }} />{l}</button>
        ))}
      </div>
      <div className="ph-agenda" ref={list} role="feed" aria-label="Agenda" aria-busy={q.isFetching}>
        {!all.length && q.data === undefined && !shown.current ? <SkelList rows={5} /> : <>
          <div className="ph-calnote ph-calend">
            <span>{earlier ? 'Loading earlier days' : `From ${dayLabel(range.from)}.`}</span>
            <button type="button" className="ph-todayb" disabled={earlier} onClick={() => { setEarlier(true); setBlocks(b => ({ ...b, lo: b.lo - 1 })); }}>Show earlier days</button>
          </div>
          {days.map(d => (
            <section key={d} data-day={d} role="article" aria-label={longDay(d)} className="ph-dsec">
              {note?.before === d && noteEl(`n-${d}`)}
              <h2 className="ph-dayh">{longDay(d)}{d === today && <span className="tdy">Today</span>}</h2>
              <div className="ph-grp">
                {byDay.get(d)!.map(i => (
                  <button key={`${i.kind}-${i.id}`} type="button" className={`ph-row ph-ev${i.done ? ' done' : ''}${i.kind === 'reminder' && i.aiSet ? ' ai' : ''}`} onClick={() => setOpen(i)} data-kind={i.kind}>
                    <span className="etm"><span>{i.start ?? 'All day'}</span>{i.end && <span>{i.end}</span>}</span>
                    <span className="ebar" style={{ background: colours.c(i) }} />
                    <span className="ph-rt"><div className="t">{i.isMilestone && <span className="ph-dia" aria-label="Milestone" />}{i.title}</div>
                      <div className="s">{[typeWord(i), i.trackName, i.owner, i.waitingOn ? `Waiting on ${i.waitingOn}` : null].filter(Boolean).join(' · ')}</div></span>
                  </button>
                ))}
              </div>
            </section>
          ))}
          {note && note.before === null && days.length > 0 && noteEl('n-end')}
          {!days.length && (
            <div className="ph-empty2">
              Nothing on the calendar from {dayLabel(range.from)} to {dayLabel(range.to)}{hidden.length ? '. Some kinds are hidden: tap a chip to show them.' : mine ? ' that is yours. View, Only mine, shows everyone\'s.' : '.'}
            </div>
          )}
          <div className="ph-calnote ph-calend">
            <span>{q.isFetching && !earlier ? 'Loading more days' : `Up to ${dayLabel(range.to)}.`}</span>
            <button type="button" className="ph-todayb" disabled={q.isFetching} onClick={() => setBlocks(b => ({ ...b, hi: b.hi + 1 }))}>Show later days</button>
          </div>
        </>}
      </div>
      <AnimatePresence>
        {open && (
          <PSheet key="ev" label={open.title} onClose={() => setOpen(null)}>
            <div className="rowc gap6"><Tag dot>{typeWord(open)}</Tag>{open.isMilestone && <Tag>Milestone</Tag>}{open.waitingOn && <Tag tone="a">Waiting on {open.waitingOn}</Tag>}</div>
            <h2>{open.title}</h2>
            <div className="ph-grp">
              <div className="ph-kv"><span>When</span><span>{dayLabel(open.date)}{open.start ? `, ${open.start}${open.end ? ` to ${open.end}` : ''}` : ''}</span></div>
              {open.trackName && <div className="ph-kv"><span>Track</span><span>{open.trackName}</span></div>}
              {open.spaceName && <div className="ph-kv"><span>Space</span><span>{open.spaceName}</span></div>}
              {open.owner && <div className="ph-kv"><span>Owner</span><span>{open.owner}</span></div>}
            </div>
            {open.kind === 'reminder' ? <Link className="ph-btf" to="/reminders">Open reminders</Link> : <>
              <button type="button" className="ph-btf" onClick={() => { const id = open.activityId; setOpen(null); if (id) acts.open(id); }}>Open activity</button>
              <button type="button" className="ph-btf sec" onClick={() => panel.open({ activityId: open.activityId, label: open.title, prompt: `Tell me about ${open.title}`, send: true })}>Ask about it</button>
            </>}
          </PSheet>
        )}
        {viewing && (
          <PSheet key="view" label="View" onClose={() => setViewing(false)}>
            <h2>View</h2>
            <div className="xs mut">Colour events by</div>
            <Seg label="Colour events by" value={by} onChange={setBy} options={[['type', 'Type'], ['track', 'Track']] as const} />
            <div className="xs mut">Space</div>
            <div className="ph-chips" role="group" aria-label="Space">
              <button type="button" className={`ph-chip${!spaceId ? ' on' : ''}`} aria-pressed={!spaceId} onClick={() => setSpaceId('')}>All spaces</button>
              {spaces.map(s => <button key={s.id} type="button" className={`ph-chip${spaceId === s.id ? ' on' : ''}`} aria-pressed={spaceId === s.id} onClick={() => setSpaceId(s.id)}>{s.name}</button>)}
            </div>
            <div className="ph-grp">
              <button type="button" className="ph-row" role="switch" aria-checked={mine} onClick={() => setMine(!mine)}>
                <span className="ph-rt"><div className="t">Only mine</div><div className="s">Things you own or attend</div></span><span className={`ph-sw${mine ? ' on' : ''}`} aria-hidden="true" />
              </button>
            </div>
            <button type="button" className="ph-btf" onClick={() => setViewing(false)}>Done</button>
          </PSheet>
        )}
      </AnimatePresence>
    </div>
  );
}
